import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';

import AssistantDefaultsModal from '../components/AssistantDefaultsModal';
import api from '../api/axios';
import { tokenCanManageUsers } from '../utils/sessionUser';

const statusLabels = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  step_1: 'Step 1',
  deployed: 'Deployed',
};

const statusColors = {
  not_started: 'default',
  in_progress: 'warning',
  step_1: 'info',
  deployed: 'success',
};

const stepLabels = {
  step_1: 'Curriculum Analysis',
  step_2: 'Qualification Overview',
  step_3: 'ELO Mapping Canvas',
  step_4: 'Storyboard Creation',
  step_5: 'Content Creation',
  step_6: 'Experience Verification',
  step_7: 'Contact Sessions',
  step_8: 'Exam Preparation',
  step_9: 'Mystery Student Report',
  'N/A': 'N/A'
};

const DashboardPage = () => {
  const [qualifications, setQualifications] = useState([]);
  const [canManageUsers, setCanManageUsers] = useState(() => tokenCanManageUsers(localStorage.getItem('token')));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stepStatuses, setStepStatuses] = useState({}); // { [qualificationId]: [{step_key, status}, ...] }
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    const loadQualifications = async () => {
      try {
        const res = await api.get('/qualifications');
        setQualifications(res.data);
        
        // Fetch step statuses for each qualification
        const statuses = {};
        await Promise.all(res.data.map(async (q) => {
          try {
            const resp = await api.get(`/qualifications/${q.id}/steps`);
            statuses[q.id] = resp.data;
          } catch (e) {
            statuses[q.id] = [];
          }
        }));
        setStepStatuses(statuses);
      } catch (error) {

        navigate('/login');
      }
    };
    
    loadQualifications();
    const signedInCanManage = tokenCanManageUsers(token);
    if (signedInCanManage) setCanManageUsers(true);
    api.get('/auth/me')
      .then((res) => setCanManageUsers(!!res.data.canManageUsers || signedInCanManage))
      .catch(() => setCanManageUsers(signedInCanManage));
    
    // Refresh on focus (when returning from qualification page)
    const handleFocus = () => {

      loadQualifications();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [navigate]);

  // Function to refresh step statuses for a specific qualification
  const refreshQualificationStatus = async (qualificationId) => {
    try {
      const resp = await api.get(`/qualifications/${qualificationId}/steps`);
      setStepStatuses(prev => ({
        ...prev,
        [qualificationId]: resp.data
      }));
    } catch (e) {

    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/qualifications/${id}`);
    setQualifications(qs => qs.filter(q => q.id !== id));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Helper to get current step and status
  const getCurrentStepInfo = (steps) => {
    if (!steps || steps.length === 0) return { step: 'N/A', status: 'N/A' };
    // Find first not completed, or last completed
    const notCompleted = steps.find(s => s.status !== 'completed');
    if (notCompleted) return { step: notCompleted.step_key, status: notCompleted.status };
    return { step: steps[steps.length - 1].step_key, status: steps[steps.length - 1].status };
  };

  // Helper to calculate overall qualification status based on step completion
  const getOverallStatus = (steps) => {
    if (!steps || steps.length === 0) return 'not_started';
    
    const totalSteps = steps.length;
    const completedSteps = steps.filter(s => s.status === 'completed').length;
    const inProgressSteps = steps.filter(s => s.status === 'in_progress').length;
    
    // If all steps are completed
    if (completedSteps === totalSteps) {
      return 'deployed';
    }
    
    // If any step is in progress or some steps are completed
    if (inProgressSteps > 0 || completedSteps > 0) {
      return 'in_progress';
    }
    
    // If no steps are started
    return 'not_started';
  };

  return (
    <Container>
      <Box mt={4} mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">Qualifications</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            AI Assistant Settings
          </Button>
          {canManageUsers && (
            <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => navigate('/users')}>
              Users
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/add')}>Add Qualification</Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>
      </Box>
      <AssistantDefaultsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Current Step</TableCell>
              <TableCell>Step Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {qualifications.map(q => {
              const steps = stepStatuses[q.id] || [];
              const { step, status } = getCurrentStepInfo(steps);
              const overallStatus = getOverallStatus(steps);
              return (
                <TableRow key={q.id}>
                  <TableCell>{q.title}</TableCell>
                  <TableCell>
                    <Chip label={statusLabels[overallStatus]} color={statusColors[overallStatus]} />
                  </TableCell>
                  <TableCell>{stepLabels[step] || step}</TableCell>
                  <TableCell>{status}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton onClick={() => navigate(`/edit/${q.id}`)}><EditIcon /></IconButton>
                      <IconButton onClick={() => handleDelete(q.id)}><DeleteIcon /></IconButton>
                      <IconButton onClick={() => navigate(`/qualification/${q.id}`)}><ArrowForwardIcon /></IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default DashboardPage; 